import React, { useState } from 'react';
import {
  Paper, Tabs, Tab, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, Chip
} from '@mui/material';

const ChangesTable = ({ data }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Paper elevation={2}>
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        {data.transactions && <Tab label={`Transactions (${data.transactions.recent?.length || 0})`} />}
        {data.investments && <Tab label={`Investments (${data.investments.recent?.length || 0})`} />}
        {data.subscriptions && <Tab label={`Subscriptions (${data.subscriptions.recent?.length || 0})`} />}
        {data.groups && <Tab label={`Groups (${data.groups.recent?.length || 0})`} />}
        {data.users && <Tab label={`Users (${data.users.recent?.length || 0})`} />}
      </Tabs>

      {/* Transactions Tab */}
      {data.transactions && activeTab === 0 && (
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Recent Transactions
          </Typography>
          
          {data.transactions.by_type && (
            <Box mb={2}>
              {data.transactions.by_type.map((type, index) => (
                <Chip
                  key={index}
                  label={`${type.transaction_type}: ${type.count}`}
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Subscription No</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.transactions.recent?.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.id}</TableCell>
                    <TableCell>
                      <Chip label={record.transaction_type} size="small" color="primary" />
                    </TableCell>
                    <TableCell>₹{record.amount}</TableCell>
                    <TableCell>{record.investment__subscription__subscription_no}</TableCell>
                    <TableCell>{record.investment__subscription__group__title}</TableCell>
                    <TableCell>{new Date(record.timestamp).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Investments Tab */}
      {data.investments && activeTab === (data.transactions ? 1 : 0) && (
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Recent Investments
          </Typography>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Investment No</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.investments.recent?.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.id}</TableCell>
                    <TableCell>{record.investment_no}</TableCell>
                    <TableCell>{record.group_title}</TableCell>
                    <TableCell>
                      <Chip label={record.status} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{record.created_at}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Subscriptions Tab */}
      {data.subscriptions && activeTab === ((data.transactions ? 1 : 0) + (data.investments ? 1 : 0)) && (
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Recent Subscriptions
          </Typography>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Subscription No</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.subscriptions.recent?.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.id}</TableCell>
                    <TableCell>{record.subscription_no}</TableCell>
                    <TableCell>{record.group_title}</TableCell>
                    <TableCell>₹{record.subscription_amt}</TableCell>
                    <TableCell>{record.created_at}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Groups Tab */}
      {data.groups && activeTab === ((data.transactions ? 1 : 0) + (data.investments ? 1 : 0) + (data.subscriptions ? 1 : 0)) && (
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Recent Groups
          </Typography>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Max Users</TableCell>
                  <TableCell>Subscription Amount</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>From Migrator</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.groups.recent?.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.id}</TableCell>
                    <TableCell><Chip label={record.title} size="small" color="secondary" /></TableCell>
                    <TableCell>{record.max_users}</TableCell>
                    <TableCell>₹{record.subscription_amt}</TableCell>
                    <TableCell>{new Date(record.start_date).toLocaleDateString()}</TableCell>
                    <TableCell>{record.end_date ? new Date(record.end_date).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      <Chip label={record.is_from_migrator ? 'Yes' : 'No'} size="small" color={record.is_from_migrator ? 'info' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Users Tab */}
      {data.users && activeTab === ((data.transactions ? 1 : 0) + (data.investments ? 1 : 0) + (data.subscriptions ? 1 : 0) + (data.groups ? 1 : 0)) && (
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Recent Users
          </Typography>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Date Joined</TableCell>
                  <TableCell>From Migrator</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.users.recent?.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>{record.id}</TableCell>
                    <TableCell>{record.full_name}</TableCell>
                    <TableCell>{record.phone_no}</TableCell>
                    <TableCell>{record.email}</TableCell>
                    <TableCell>{new Date(record.date_joined).toLocaleString()}</TableCell>
                    <TableCell>
                      <Chip label={record.is_from_migrator ? 'Yes' : 'No'} size="small" color={record.is_from_migrator ? 'info' : 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Paper>
  );
};

export default ChangesTable;
